import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText } from 'lucide-react'
import {
  paqueteMensualService,
  type PaquetePendienteDeRevision,
} from '@/features/proveedores/api/paqueteMensual.service'
import { nombreDelMes } from '@/features/proveedores/lib/mesesDelPaquete'
import { formatDate } from '@/shared/lib/dateUtils'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

/**
 * Bandeja de paquetes mensuales esperando revisión (SP-219).
 *
 * Al enviar su paquete el proveedor no genera notificación a Contraloría —
 * decisión de SP-220—, así que esta pantalla es la única forma de enterarse
 * de quién está esperando. Revisar se hace en el expediente del proveedor,
 * que es donde vive el tab con los 14 documentos.
 */
export default function PaquetesMensualesPendientes() {
  const navigate = useNavigate()
  const [paquetes, setPaquetes] = useState<PaquetePendienteDeRevision[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    paqueteMensualService
      .pendientesDeRevision()
      .then(setPaquetes)
      .catch(() => setError('No se pudo cargar el listado de paquetes pendientes.'))
      .finally(() => setCargando(false))
  }, [])

  const nombreDelProveedor = (paquete: PaquetePendienteDeRevision) =>
    paquete.supplier?.legalName || paquete.supplier?.commercialName || `Proveedor ${paquete.supplierId}`

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/super-admin-proveedores')}
          className={estiloDeBoton('secundario', 'mb-4')}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Paquetes mensuales por revisar</h1>
          <p className="text-[#666666]">
            Proveedores de Servicios que ya enviaron sus documentos del mes y esperan revisión
          </p>
        </div>

        {cargando && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#E5E7EB] border-t-[#2563EB]" />
          </div>
        )}

        {!cargando && error && (
          <div className="rounded-lg border border-[#F5C6CB] bg-[#FBEEEE] px-4 py-3 text-sm text-[#EF4444]">
            {error}
          </div>
        )}

        {!cargando && !error && paquetes.length === 0 && (
          <div className="bg-white rounded-lg border border-[#E5E7EB] py-16 text-center">
            <FileText className="w-10 h-10 text-[#C9CDD3] mx-auto mb-3" />
            <p className="text-[#1B2128] font-medium">No hay paquetes esperando revisión</p>
            <p className="text-sm text-[#666666] mt-1">
              Aquí aparecen los proveedores en cuanto envían sus documentos del mes.
            </p>
          </div>
        )}

        {!cargando && !error && paquetes.length > 0 && (
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#E5E7EB]">
                <thead className="bg-[#FAF9FF]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Proveedor</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">RFC</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Mes</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Enviado</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E1E1E1]">
                  {paquetes.map((paquete) => (
                    <tr key={paquete.id} className="hover:bg-[#F4F7FB] transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-[#1B2128]">
                        {nombreDelProveedor(paquete)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#666666]">{paquete.supplier?.rfc || '—'}</td>
                      <td className="px-6 py-4 text-sm text-[#1B2128] first-letter:uppercase">
                        {nombreDelMes(paquete.month, paquete.year)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#666666]">
                        {paquete.updatedAt ? formatDate(paquete.updatedAt) : '—'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() =>
                            navigate(`/super-admin-proveedores/${paquete.supplierId}?tab=mensual`)
                          }
                          className={estiloDeBoton('primario')}
                        >
                          Revisar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
